import { useCallback, useState } from 'react'
import { useLocation } from 'react-router'

import {
  IconButton,
  type IconProps,
  useFvNavigate,
} from '@fv/client-components'
import type { AuditStatus } from '@fv/models'

import { DividedContent } from '@/components/DividedContent'
import ExportOptions from '@/components/ExportOptions'

import { HasPermission } from '../auth'
import { SortDirectionToggle } from '../shipment-list/SortDirectionToggle'
import AuditListTab from './AuditListTab'
import { AuditSortSelect } from './AuditSortSelect'
import {
  AUDIT_LOCAL_STORAGE_SORTDIR_KEY,
  useAuditListSort,
  useAuditListStatus,
} from './queries'

const auditListTabs = [
  {
    icon: 'file-invoice-dollar' as IconProps['icon'],
    label: 'Outstanding',
    status: 'outstanding' as AuditStatus,
  },
  {
    icon: 'archive' as IconProps['icon'],
    iconClassName: 'text-fv-green-bright',
    label: 'Accepted',
    status: 'accepted' as AuditStatus,
  },
  {
    icon: 'archive' as IconProps['icon'],
    iconClassName: 'text-fv-orange-bright',
    label: 'Disputed',
    status: 'disputed' as AuditStatus,
  },
  {
    icon: 'archive' as IconProps['icon'],
    label: 'Archived',
    status: 'archived' as AuditStatus,
  },
]

export const AuditListHeader = () => {
  const { search } = useLocation()
  const [isExporting, setExporting] = useState(false)
  const navigate = useFvNavigate()
  const status = useAuditListStatus()
  const { sortBy, sortDirection } = useAuditListSort(status)
  const exportParams = new URLSearchParams(search)

  exportParams.set('auditStatus[]', status ?? '')
  exportParams.delete('status[]')

  const onClick = useCallback(
    (e: any) => {
      e.preventDefault()
      const tabStatus = e.currentTarget.id as AuditStatus

      if (tabStatus !== status) {
        const params = new URLSearchParams(search)
        params.set('status[]', tabStatus)
        navigate(`/audit?${params.toString()}`)
      }
    },
    [navigate, search, status],
  )

  return (
    <DividedContent className="mb-8">
      {auditListTabs.map(t => (
        <AuditListTab
          {...t}
          isActive={t.status === status}
          key={t.status}
          onClick={onClick}
        />
      ))}

      <div className="flex items-center gap-1">
        <SortDirectionToggle
          localStorageKey={AUDIT_LOCAL_STORAGE_SORTDIR_KEY}
          sortDirection={sortDirection}
        />
        <span>by</span>
        <AuditSortSelect
          value={sortBy}
          includeAccepted={status === 'accepted'}
        />
      </div>

      <HasPermission name="load-exporter">
        <IconButton icon="file-export" onClick={() => setExporting(true)}>
          Export list
        </IconButton>

        <ExportOptions
          closePanel={() => setExporting(false)}
          isOpen={isExporting}
          queryString={exportParams.toString()}
        />
      </HasPermission>
    </DividedContent>
  )
}
